import { Add, CenterFocusStrong, Remove, RestartAlt, Save, Undo } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useBlocker } from '@tanstack/react-router';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  ConnectionMode,
  getStraightPath,
  Handle,
  MarkerType,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useInternalNode,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
  type IsValidConnection,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { EntitySummary, EntityType, Relationship } from '@/lib/types';

import {
  CLASSIC_RELATIONSHIP_NODE_SIZE,
  getDefaultRelationshipMapShapeTheme,
  getRelationshipMapShapeThemeStorageKey,
  getRelationshipMapSurface,
  getRelationshipNodeAccent,
  getRelationshipNodeDimensions,
  isRelationshipMapShapeTheme,
  RELATIONSHIP_MAP_SHAPE_THEMES,
  RelationshipGraphNodeShape,
  type RelationshipMapShapeTheme,
} from '@/components/relationship-graph-node-shapes';
import { useUser } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { useNovelRepository } from '@/hooks/useNovelRepository';

import '@xyflow/react/dist/style.css';

const NODE_WIDTH = CLASSIC_RELATIONSHIP_NODE_SIZE.width;
const NODE_HEIGHT = CLASSIC_RELATIONSHIP_NODE_SIZE.height;
const FALLBACK_COLUMNS = 4;
const ORGANIGRAM_SNAP_DISTANCE = 20;
const elk = new ELK();

export type GraphPosition = {
  x: number;
  y: number;
};

export type RelationshipGraphShapeTheme = RelationshipMapShapeTheme;

export type RelationshipGraphEntity = EntitySummary & {
  entityType: string;
  graphPosition?: GraphPosition;
  graphPositionUpdatedBy?: string;
  graphPositionUpdatedAt?: string;
  graphTypeLabel?: string;
  graphShapeEntityType?: string;
  graphAccentColor?: string;
  graphOutlineColor?: string;
};

export type RelationshipGraphConnectionEndpoint = {
  entityId: string;
  entityType: string;
};

type RelationshipNodeData = {
  label: ReactNode;
  entityId: string;
  entityType: string;
  shapeEntityType: string;
  color: string;
};

type RelationshipNode = Node<RelationshipNodeData>;

function RelationshipEntityNode({ data, isConnectable }: NodeProps<RelationshipNode>) {
  return (
    <>
      <Handle type="source" position={Position.Left} isConnectable={isConnectable} />
      {data.label}
      <Handle type="source" position={Position.Right} isConnectable={isConnectable} />
    </>
  );
}

const relationshipNodeTypes: NodeTypes = {
  relationshipEntity: RelationshipEntityNode,
};

interface RelationshipGraphProps {
  entities: RelationshipGraphEntity[];
  relationships: Relationship[];
  codexCategories: Record<string, string>;
  hiddenEntityTypeIds?: ReadonlySet<string>;
  novelId?: string | null;
  novelTheme?: string | null;
  shapeThemeOverride?: RelationshipGraphShapeTheme | null;
  onShapeThemeChange?: (shapeTheme: RelationshipGraphShapeTheme) => void | Promise<void>;
  onEntityClick?: (entityType: string, entityId: string) => void;
  onRelationshipClick?: (relationship: Relationship) => void;
  enableLayoutEditing?: boolean;
  enableRelationshipEditing?: boolean;
  showShapeThemeSelector?: boolean;
  showEntityTooltip?: boolean;
  onSaveLayoutPositions?: (positions: Record<string, GraphPosition>) => Promise<void>;
  onLayoutPositionsChange?: (positions: Record<string, GraphPosition>) => void;
  fitViewDependency?: unknown;
  suppressLayoutNavigationBlocker?: boolean;
  isRelationshipSaving?: boolean;
  onCreateRelationship?: (
    source: RelationshipGraphConnectionEndpoint,
    target: RelationshipGraphConnectionEndpoint,
  ) => void;
  isValidRelationshipConnection?: (
    source: RelationshipGraphConnectionEndpoint,
    target: RelationshipGraphConnectionEndpoint,
    connection: Connection | Edge,
  ) => boolean;
  height?: number | string;
  minHeight?: number;
}

const getNodeId = (entityType: string, entityId: string) => `${entityType}:${entityId}`;

const getEntityTypeFilterId = (entity: RelationshipGraphEntity) => (
  entity.entityType === 'codex' && entity.type
    ? `codex:${entity.type}`
    : entity.entityType
);

function getPositionMap(nodes: RelationshipNode[]): Map<string, GraphPosition> {
  return new Map(nodes.map((node) => [
    node.id,
    { x: node.position.x, y: node.position.y },
  ]));
}

function getPositionRecord(nodes: RelationshipNode[]): Record<string, GraphPosition> {
  return Object.fromEntries(nodes.map((node) => [
    node.id,
    { x: node.position.x, y: node.position.y },
  ]));
}

function applyPositionMap(nodes: RelationshipNode[], positions: Map<string, GraphPosition>) {
  return nodes.map((node) => ({
    ...node,
    position: positions.get(node.id) || node.position,
  }));
}

function arePositionsEqual(a?: GraphPosition, b?: GraphPosition) {
  if (!a || !b) return !a && !b;
  return a.x === b.x && a.y === b.y;
}

function getInitialRelationshipMapShapeTheme({
  novelId,
  novelTheme,
  shapeThemeOverride,
}: {
  novelId?: string | null;
  novelTheme?: string | null;
  shapeThemeOverride?: RelationshipGraphShapeTheme | null;
}): RelationshipMapShapeTheme {
  if (shapeThemeOverride && isRelationshipMapShapeTheme(shapeThemeOverride)) {
    return shapeThemeOverride;
  }

  if (typeof window === 'undefined') return getDefaultRelationshipMapShapeTheme(novelTheme);

  try {
    const storedTheme = window.localStorage.getItem(getRelationshipMapShapeThemeStorageKey(novelId));
    return isRelationshipMapShapeTheme(storedTheme)
      ? storedTheme
      : getDefaultRelationshipMapShapeTheme(novelTheme);
  } catch {
    return getDefaultRelationshipMapShapeTheme(novelTheme);
  }
}

function getEllipseBoundaryPoint(
  center: GraphPosition,
  deltaX: number,
  deltaY: number,
  width: number,
  height: number,
  direction: 1 | -1,
) {
  const radiusX = width / 2;
  const radiusY = height / 2;
  const scale = 1 / Math.sqrt(
    (deltaX * deltaX) / (radiusX * radiusX)
    + (deltaY * deltaY) / (radiusY * radiusY),
  );

  return {
    x: center.x + direction * deltaX * scale,
    y: center.y + direction * deltaY * scale,
  };
}

function CircularFloatingEdge({
  id,
  source,
  target,
  markerStart,
  markerEnd,
  style,
  data,
}: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) return null;

  const sourceWidth = sourceNode.measured.width || NODE_WIDTH;
  const sourceHeight = sourceNode.measured.height || NODE_HEIGHT;
  const targetWidth = targetNode.measured.width || NODE_WIDTH;
  const targetHeight = targetNode.measured.height || NODE_HEIGHT;
  const sourceCenter = {
    x: sourceNode.internals.positionAbsolute.x + sourceWidth / 2,
    y: sourceNode.internals.positionAbsolute.y + sourceHeight / 2,
  };
  const targetCenter = {
    x: targetNode.internals.positionAbsolute.x + targetWidth / 2,
    y: targetNode.internals.positionAbsolute.y + targetHeight / 2,
  };
  const deltaX = targetCenter.x - sourceCenter.x;
  const deltaY = targetCenter.y - sourceCenter.y;
  const distance = Math.hypot(deltaX, deltaY);

  if (distance === 0) return null;

  const sourcePoint = getEllipseBoundaryPoint(sourceCenter, deltaX, deltaY, sourceWidth, sourceHeight, 1);
  const targetPoint = getEllipseBoundaryPoint(targetCenter, deltaX, deltaY, targetWidth, targetHeight, -1);
  const [straightPath] = getStraightPath({
    sourceX: sourcePoint.x,
    sourceY: sourcePoint.y,
    targetX: targetPoint.x,
    targetY: targetPoint.y,
  });
  const targetIsBelow = targetCenter.y >= sourceCenter.y;
  const orthogonalSourcePoint = {
    x: sourceCenter.x,
    y: sourceCenter.y + (targetIsBelow ? sourceHeight / 2 : -sourceHeight / 2),
  };
  const orthogonalTargetPoint = {
    x: targetCenter.x,
    y: targetCenter.y + (targetIsBelow ? -targetHeight / 2 : targetHeight / 2),
  };
  const middleY = orthogonalSourcePoint.y
    + (orthogonalTargetPoint.y - orthogonalSourcePoint.y) / 2;
  const edgePath = data?.connectorStyle === 'orthogonal'
    ? `M ${orthogonalSourcePoint.x},${orthogonalSourcePoint.y} V ${middleY} H ${orthogonalTargetPoint.x} V ${orthogonalTargetPoint.y}`
    : straightPath;

  return (
    <BaseEdge
      id={id}
      path={edgePath}
      markerStart={markerStart}
      markerEnd={markerEnd}
      style={style}
      interactionWidth={16}
    />
  );
}

const relationshipEdgeTypes = {
  circularFloating: CircularFloatingEdge,
} as EdgeTypes;

async function layoutGraph(
  nodes: RelationshipNode[],
  edges: Edge[],
  shapeTheme: RelationshipMapShapeTheme,
) {
  const graph = await elk.layout({
    id: 'relationship-map',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '70',
      'elk.layered.spacing.nodeNodeBetweenLayers': '130',
    },
    children: nodes.map((node) => {
      const dimensions = getRelationshipNodeDimensions(node.data.shapeEntityType || node.data.entityType, shapeTheme);

      return {
        id: node.id,
        width: dimensions.width,
        height: dimensions.height,
      };
    }),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  });

  const positions = new Map(
    (graph.children || []).map((node) => [node.id, { x: node.x || 0, y: node.y || 0 }]),
  );

  return nodes.map((node) => ({
    ...node,
    position: positions.get(node.id) || node.position,
  }));
}

function RelationshipGraphCanvas({
  entities,
  relationships,
  codexCategories,
  hiddenEntityTypeIds,
  novelId,
  novelTheme,
  shapeThemeOverride,
  onShapeThemeChange,
  onEntityClick,
  onRelationshipClick,
  enableLayoutEditing = true,
  enableRelationshipEditing = false,
  showShapeThemeSelector = true,
  showEntityTooltip = true,
  fitViewDependency,
  onSaveLayoutPositions,
  onLayoutPositionsChange,
  suppressLayoutNavigationBlocker = false,
  isRelationshipSaving = false,
  onCreateRelationship,
  isValidRelationshipConnection,
}: RelationshipGraphProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const { updateEntityItem } = useNovelRepository();
  const { user } = useUser();
  const { userData, updateProfile, isLoading: isUserProfileLoading } = useUserProfile();
  const [shapeTheme, setShapeTheme] = useState<RelationshipMapShapeTheme>(
    () => getInitialRelationshipMapShapeTheme({
      novelId,
      novelTheme,
      shapeThemeOverride,
    }),
  );
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [isResettingLayout, setIsResettingLayout] = useState(false);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const mapSurface = useMemo(
    () => getRelationshipMapSurface(shapeTheme, theme),
    [shapeTheme, theme],
  );

  const graphNodes = useMemo<RelationshipNode[]>(() => {
    const entityColors: Record<string, string> = {
      characters: theme.palette.primary.main,
      locations: theme.palette.success.main,
      organizations: theme.palette.warning.main,
      events: theme.palette.error.main,
      codex: theme.palette.info.main,
    };

    const entityTypeLabels: Record<string, string> = {
      characters: t('entity_list.characters.title'),
      locations: t('entity_list.locations.title'),
      organizations: t('entity_list.organizations.title'),
      events: t('entity_list.events.title'),
    };

    const entitiesByNodeId = new Map(
      entities.map((entity) => [getNodeId(entity.entityType, entity.id), entity]),
    );
    const isEntityHidden = (entity: RelationshipGraphEntity) => (
      hiddenEntityTypeIds?.has(getEntityTypeFilterId(entity)) ?? false
    );
    const relationshipsByNodeId = new Map<string, string[]>();

    const addRelationshipDescription = (nodeId: string, description: string) => {
      const descriptions = relationshipsByNodeId.get(nodeId) || [];
      descriptions.push(description);
      relationshipsByNodeId.set(nodeId, descriptions);
    };

    relationships.forEach((relationship) => {
      const sourceNodeId = getNodeId(relationship.entityAType, relationship.entityAId);
      const targetNodeId = getNodeId(relationship.entityBType, relationship.entityBId);
      const sourceEntity = entitiesByNodeId.get(sourceNodeId);
      const targetEntity = entitiesByNodeId.get(targetNodeId);

      if (sourceEntity && targetEntity && !isEntityHidden(sourceEntity) && !isEntityHidden(targetEntity)) {
        addRelationshipDescription(
          sourceNodeId,
          `${relationship.forwardDescription} → ${targetEntity.name}`,
        );
        addRelationshipDescription(
          targetNodeId,
          `${relationship.reverseDescription?.trim() || t('relationships.related_to')} → ${sourceEntity.name}`,
        );
      }
    });

    const sortedEntities = [...entities].sort((entityA, entityB) => (
      getNodeId(entityA.entityType, entityA.id).localeCompare(
        getNodeId(entityB.entityType, entityB.id),
      )
    ));

    const occupiedRects: Array<{ x: number; y: number; width: number; height: number }> = [];

    // Pre-populate with explicitly saved graph positions to ensure fallback nodes don't collide with them.
    sortedEntities.forEach((entity) => {
      if (entity.graphPosition) {
        const shapeEntityType = entity.graphShapeEntityType || entity.entityType;
        const dimensions = getRelationshipNodeDimensions(shapeEntityType, shapeTheme);
        occupiedRects.push({
          x: entity.graphPosition.x,
          y: entity.graphPosition.y,
          width: dimensions.width,
          height: dimensions.height,
        });
      }
    });

    return sortedEntities.map((entity) => {
      const color = entityColors[entity.entityType] || theme.palette.grey[500];
      const shapeEntityType = entity.graphShapeEntityType || entity.entityType;
      const accent = entity.graphAccentColor || getRelationshipNodeAccent(shapeTheme, shapeEntityType, color);
      const outline = entity.graphOutlineColor || accent;
      const dimensions = getRelationshipNodeDimensions(shapeEntityType, shapeTheme);
      const isClassicShapeTheme = shapeTheme === 'classic';
      const nodeId = getNodeId(entity.entityType, entity.id);
      const typeLabel = entity.graphTypeLabel || (entity.entityType === 'codex'
        ? (codexCategories[entity.type] || entity.type)
        : (entityTypeLabels[entity.entityType] || entity.entityType));
      const relationshipDescriptions = relationshipsByNodeId.get(nodeId) || [];
      const imageSource = entity.mainImageUrl || entity.mainImagePath || '';
      const initials = entity.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

      const position = entity.graphPosition || (() => {
        let gridIndex = 0;
        while (true) {
          const candidate = {
            x: (gridIndex % FALLBACK_COLUMNS) * (NODE_WIDTH + 80),
            y: Math.floor(gridIndex / FALLBACK_COLUMNS) * (NODE_HEIGHT + 70),
          };

          const candidateRect = {
            x: candidate.x,
            y: candidate.y,
            width: dimensions.width,
            height: dimensions.height,
          };

          const hasOverlap = occupiedRects.some((rect) => {
            const margin = 40; // Maintain breathing room
            return !(
              candidateRect.x + candidateRect.width + margin <= rect.x ||
              candidateRect.x >= rect.x + rect.width + margin ||
              candidateRect.y + candidateRect.height + margin <= rect.y ||
              candidateRect.y >= rect.y + rect.height + margin
            );
          });

          if (!hasOverlap) {
            occupiedRects.push(candidateRect);
            return candidate;
          }
          gridIndex++;
        }
      })();

      const nodeShape = (
        <RelationshipGraphNodeShape
          shapeTheme={shapeTheme}
          entityType={shapeEntityType}
          name={entity.name}
          typeLabel={typeLabel}
          color={color}
          accentColor={entity.graphAccentColor}
          outlineColor={entity.graphOutlineColor}
          imageSource={imageSource}
          imagePath={entity.mainImagePath}
          initials={initials}
        />
      );

      return {
        id: nodeId,
        type: 'relationshipEntity',
        position,
        data: {
          entityId: entity.id,
          entityType: entity.entityType,
          shapeEntityType,
          color: accent,
          label: showEntityTooltip ? (
            <Tooltip
              arrow
              placement="top"
              enterDelay={250}
              title={(
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {entity.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mb: 1 }}>
                    {typeLabel}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                    {t('relationships.node_relationships')}
                  </Typography>
                  {relationshipDescriptions.length > 0 ? relationshipDescriptions.map((description, relationshipIndex) => (
                    <Typography
                      key={`${description}-${relationshipIndex}`}
                      variant="caption"
                      sx={{ display: 'block', '& + &': { mt: 0.5 } }}
                    >
                      {description}
                    </Typography>
                  )) : (
                    <Typography variant="caption">{t('relationships.node_no_relationships')}</Typography>
                  )}
                </Box>
              )}
              slotProps={{ tooltip: { sx: { maxWidth: 340 } } }}
            >
              {nodeShape}
            </Tooltip>
          ) : nodeShape,
        },
        style: {
          width: dimensions.width,
          height: dimensions.height,
          padding: 0,
          borderRadius: isClassicShapeTheme ? '50%' : 0,
          border: isClassicShapeTheme ? `3px solid ${outline}` : 'none',
          background: isClassicShapeTheme ? theme.palette.background.paper : 'transparent',
          color: theme.palette.text.primary,
          boxShadow: isClassicShapeTheme ? theme.shadows[2] : 'none',
          cursor: onEntityClick ? 'pointer' : 'grab',
          overflow: 'visible',
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        connectable: enableRelationshipEditing && !isRelationshipSaving,
        ariaLabel: `${entity.name}, ${typeLabel}`,
        hidden: isEntityHidden(entity),
      };
    });
  }, [codexCategories, enableRelationshipEditing, entities, hiddenEntityTypeIds, isRelationshipSaving, onEntityClick, relationships, shapeTheme, showEntityTooltip, t, theme]);

  const layoutEdges = useMemo<Edge[]>(() => {
    const nodeIds = new Set(graphNodes.map((node) => node.id));

    return relationships.flatMap((relationship) => {
      const source = getNodeId(relationship.entityAType, relationship.entityAId);
      const target = getNodeId(relationship.entityBType, relationship.entityBId);

      if (!nodeIds.has(source) || !nodeIds.has(target)) return [];

      const reverseDescription = relationship.reverseDescription?.trim();
      const edge: Edge = {
        id: relationship.id,
        source,
        target,
        type: 'circularFloating',
        data: { connectorStyle: relationship.connectorStyle || 'straight' },
        markerStart: reverseDescription ? {
          type: MarkerType.ArrowClosed,
          color: mapSurface.edgeColor,
        } : undefined,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: mapSurface.edgeColor,
        },
        style: {
          stroke: mapSurface.edgeColor,
          strokeWidth: 1.5,
          cursor: onRelationshipClick ? 'pointer' : undefined,
        },
      };

      return [edge];
    });
  }, [graphNodes, mapSurface.edgeColor, onRelationshipClick, relationships]);

  const graphEdges = useMemo<Edge[]>(() => {
    const visibleNodeIds = new Set(
      graphNodes.filter((node) => !node.hidden).map((node) => node.id),
    );

    return layoutEdges.filter((edge) => (
      visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    ));
  }, [graphNodes, layoutEdges]);

  const hasVisibleEntities = useMemo(
    () => graphNodes.some((node) => !node.hidden),
    [graphNodes],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<RelationshipNode>(graphNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graphEdges);
  const hasSavedPositions = useMemo(
    () => entities.some((entity) => !!entity.graphPosition),
    [entities],
  );
  const entitiesByNodeId = useMemo(
    () => new Map(entities.map((entity) => [getNodeId(entity.entityType, entity.id), entity])),
    [entities],
  );
  const nodeById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  );

  useEffect(() => {
    if (!isUserProfileLoading) {
      setShowMiniMap(userData.showMiniMap ?? true);
    }
  }, [isUserProfileLoading, userData.showMiniMap]);

  const handleShowMiniMapChange = useCallback(async (nextValue: boolean) => {
    const previousValue = showMiniMap;
    setShowMiniMap(nextValue);
    try {
      await updateProfile({ showMiniMap: nextValue });
    } catch (error) {
      setShowMiniMap(previousValue);
      console.error('Error saving relationship graph minimap preference:', error);
    }
  }, [showMiniMap, updateProfile]);
  const orthogonalNodeIds = useMemo(() => {
    const ids = new Set<string>();
    relationships.forEach((relationship) => {
      if (relationship.connectorStyle !== 'orthogonal') return;
      ids.add(getNodeId(relationship.entityAType, relationship.entityAId));
      ids.add(getNodeId(relationship.entityBType, relationship.entityBId));
    });
    return ids;
  }, [relationships]);
  const currentUserId = user?.uid;
  const committedPositionsRef = useRef<Map<string, GraphPosition>>(new Map());
  const graphInitializedRef = useRef(false);
  const localDirtyNodeIdsRef = useRef(new Set<string>());
  const lastLocalLayoutSavedAtRef = useRef<string | null>(null);
  const connectionOriginNodeIdRef = useRef<string | null>(null);
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => enableLayoutEditing && layoutDirty && !suppressLayoutNavigationBlocker,
    enableBeforeUnload: () => enableLayoutEditing && layoutDirty && !suppressLayoutNavigationBlocker,
    withResolver: true,
  });

  useEffect(() => {
    setShapeTheme(getInitialRelationshipMapShapeTheme({
      novelId,
      novelTheme,
      shapeThemeOverride,
    }));
  }, [novelId, novelTheme, shapeThemeOverride]);

  const handleShapeThemeChange = useCallback((nextShapeTheme: RelationshipMapShapeTheme) => {
    setShapeTheme(nextShapeTheme);

    if (onShapeThemeChange) {
      try {
        void Promise.resolve(onShapeThemeChange(nextShapeTheme)).catch((error) => {
          console.error('Error saving relationship graph theme:', error);
        });
      } catch (error) {
        console.error('Error saving relationship graph theme:', error);
      }
      return;
    }

    try {
      window.localStorage.setItem(getRelationshipMapShapeThemeStorageKey(novelId), nextShapeTheme);
    } catch {
      // Theme persistence is a convenience; graph rendering should keep working without it.
    }
  }, [novelId, onShapeThemeChange]);

  const persistLayout = useCallback(async () => {
    if (!enableLayoutEditing) return false;
    if (isSavingLayout) return false;

    setIsSavingLayout(true);
    const graphPositionUpdatedAt = new Date().toISOString();
    try {
      if (onSaveLayoutPositions) {
        await onSaveLayoutPositions(getPositionRecord(nodes));
      } else {
        await Promise.all(
          nodes.map((node) => {
            const originalEntity = entitiesByNodeId.get(node.id);
            if (originalEntity && arePositionsEqual(originalEntity.graphPosition, node.position)) {
              return Promise.resolve();
            }

            const updates: Record<string, unknown> = {
              graphPosition: node.position,
              graphPositionUpdatedAt,
            };
            if (currentUserId) {
              updates.graphPositionUpdatedBy = currentUserId;
            }

            return updateEntityItem(
              node.data.entityType as EntityType,
              node.data.entityId,
              updates,
            );
          }),
        );
      }
      committedPositionsRef.current = getPositionMap(nodes);
      localDirtyNodeIdsRef.current.clear();
      lastLocalLayoutSavedAtRef.current = graphPositionUpdatedAt;
      setLayoutDirty(false);
      return true;
    } catch (error) {
      console.error('Error saving relationship graph layout:', error);
      return false;
    } finally {
      setIsSavingLayout(false);
    }
  }, [currentUserId, enableLayoutEditing, isSavingLayout, nodes, onSaveLayoutPositions, updateEntityItem]);

  const discardLayoutChanges = useCallback((options?: { focusAfterDiscard?: boolean }) => {
    if (!enableLayoutEditing) return;
    const committedPositions = committedPositionsRef.current;
    const restoredNodes = applyPositionMap(nodes, committedPositions);
    setNodes(restoredNodes);
    onLayoutPositionsChange?.(getPositionRecord(restoredNodes));
    localDirtyNodeIdsRef.current.clear();
    setLayoutDirty(false);

    if ((options?.focusAfterDiscard ?? true) && hasVisibleEntities) {
      requestAnimationFrame(() => fitView({ padding: 0.2, duration: 250 }));
    }
  }, [enableLayoutEditing, fitView, hasVisibleEntities, nodes, onLayoutPositionsChange, setNodes]);

  const handleResetLayout = useCallback(async () => {
    if (!enableLayoutEditing) return;
    if (!hasSavedPositions || !hasVisibleEntities || isResettingLayout) return;

    setIsResettingLayout(true);

    try {
      const layoutedNodes = await layoutGraph(graphNodes, layoutEdges, shapeTheme);
      setNodes(layoutedNodes);
      onLayoutPositionsChange?.(getPositionRecord(layoutedNodes));
      localDirtyNodeIdsRef.current = new Set(layoutedNodes.map((node) => node.id));
      setLayoutDirty(true);
      requestAnimationFrame(() => fitView({ padding: 0.2, duration: 350 }));
    } catch (error) {
      console.error('Error resetting relationship graph layout:', error);
    } finally {
      setIsResettingLayout(false);
    }
  }, [
    enableLayoutEditing,
    fitView,
    graphNodes,
    hasSavedPositions,
    hasVisibleEntities,
    isResettingLayout,
    layoutEdges,
    onLayoutPositionsChange,
    setNodes,
    shapeTheme,
  ]);

  const handleSaveAndLeave = useCallback(async () => {
    const saved = await persistLayout();
    if (saved) {
      proceed?.();
    }
  }, [persistLayout, proceed]);

  const handleDiscardAndLeave = useCallback(() => {
    discardLayoutChanges({ focusAfterDiscard: false });
    proceed?.();
  }, [discardLayoutChanges, proceed]);

  const connectedNodeIds = useMemo(() => {
    if (!hoveredNodeId) return null;

    const ids = new Set([hoveredNodeId]);
    edges.forEach((edge) => {
      if (edge.source === hoveredNodeId) ids.add(edge.target);
      if (edge.target === hoveredNodeId) ids.add(edge.source);
    });
    return ids;
  }, [edges, hoveredNodeId]);

  useEffect(() => {
    if (!hoveredNodeId) return;

    const hoveredNode = graphNodes.find((node) => node.id === hoveredNodeId);
    if (!hoveredNode || hoveredNode.hidden) {
      setHoveredNodeId(null);
    }
  }, [graphNodes, hoveredNodeId]);

  const focusedNodes = useMemo(() => {
    if (!connectedNodeIds) return nodes;

    return nodes.map((node) => ({
      ...node,
      style: {
        ...node.style,
        opacity: connectedNodeIds.has(node.id) ? 1 : 0.18,
        transition: 'opacity 180ms ease',
      },
    }));
  }, [connectedNodeIds, nodes]);

  const focusedEdges = useMemo(() => {
    if (!hoveredNodeId) return edges;

    return edges.map((edge) => {
      const isConnected = edge.source === hoveredNodeId || edge.target === hoveredNodeId;
      return {
        ...edge,
        style: {
          ...edge.style,
          opacity: isConnected ? 1 : 0.08,
          strokeWidth: isConnected ? 2.25 : 1.5,
          transition: 'opacity 180ms ease, stroke-width 180ms ease',
        },
      };
    });
  }, [edges, hoveredNodeId]);

  const getOrderedConnectionNodes = useCallback((connection: Connection | Edge) => {
    let sourceNode = connection.source ? nodeById.get(connection.source) : null;
    let targetNode = connection.target ? nodeById.get(connection.target) : null;

    // A drag can start from a target handle, so preserve the user's drag direction.
    if (connectionOriginNodeIdRef.current && targetNode?.id === connectionOriginNodeIdRef.current) {
      [sourceNode, targetNode] = [targetNode, sourceNode];
    }

    return { sourceNode, targetNode };
  }, [nodeById]);

  const isValidGraphConnection = useCallback<IsValidConnection>((connection) => {
    if (!enableRelationshipEditing || isRelationshipSaving) return false;

    const { sourceNode, targetNode } = getOrderedConnectionNodes(connection);
    if (!sourceNode || !targetNode) return false;
    if (sourceNode.id === targetNode.id) return false;

    const source = {
      entityId: sourceNode.data.entityId,
      entityType: sourceNode.data.entityType,
    };
    const target = {
      entityId: targetNode.data.entityId,
      entityType: targetNode.data.entityType,
    };
    const directRelationshipExists = relationships.some((relationship) => (
      relationship.entityAType === source.entityType
      && relationship.entityAId === source.entityId
      && relationship.entityBType === target.entityType
      && relationship.entityBId === target.entityId
    ));
    const reverseRelationshipCoversDirection = relationships.some((relationship) => (
      relationship.entityAType === target.entityType
      && relationship.entityAId === target.entityId
      && relationship.entityBType === source.entityType
      && relationship.entityBId === source.entityId
      && !!relationship.reverseDescription?.trim()
    ));

    if (directRelationshipExists || reverseRelationshipCoversDirection) return false;

    return isValidRelationshipConnection
      ? isValidRelationshipConnection(source, target, connection)
      : true;
  }, [
    enableRelationshipEditing,
    getOrderedConnectionNodes,
    isRelationshipSaving,
    isValidRelationshipConnection,
    relationships,
  ]);

  const handleConnect = useCallback((connection: Connection) => {
    if (!isValidGraphConnection(connection)) return;

    const { sourceNode, targetNode } = getOrderedConnectionNodes(connection);
    if (!sourceNode || !targetNode) return;

    onCreateRelationship?.(
      {
        entityId: sourceNode.data.entityId,
        entityType: sourceNode.data.entityType,
      },
      {
        entityId: targetNode.data.entityId,
        entityType: targetNode.data.entityType,
      },
    );
    connectionOriginNodeIdRef.current = null;
  }, [getOrderedConnectionNodes, isValidGraphConnection, onCreateRelationship]);

  const getSnappedNodePosition = useCallback((draggedNode: RelationshipNode) => {
    if (!orthogonalNodeIds.has(draggedNode.id)) return draggedNode.position;

    const draggedWidth = draggedNode.measured?.width || draggedNode.width || NODE_WIDTH;
    const draggedHeight = draggedNode.measured?.height || draggedNode.height || NODE_HEIGHT;
    const draggedCenterX = draggedNode.position.x + draggedWidth / 2;
    const draggedCenterY = draggedNode.position.y + draggedHeight / 2;
    let snappedX = draggedNode.position.x;
    let snappedY = draggedNode.position.y;
    let closestXDistance = ORGANIGRAM_SNAP_DISTANCE + 1;
    let closestYDistance = ORGANIGRAM_SNAP_DISTANCE + 1;

    nodes.forEach((candidate) => {
      if (candidate.id === draggedNode.id || candidate.hidden) return;

      const candidateWidth = candidate.measured?.width || candidate.width || NODE_WIDTH;
      const candidateHeight = candidate.measured?.height || candidate.height || NODE_HEIGHT;
      const candidateCenterX = candidate.position.x + candidateWidth / 2;
      const candidateCenterY = candidate.position.y + candidateHeight / 2;
      const xDistance = Math.abs(candidateCenterX - draggedCenterX);
      const yDistance = Math.abs(candidateCenterY - draggedCenterY);

      if (xDistance <= ORGANIGRAM_SNAP_DISTANCE && xDistance < closestXDistance) {
        closestXDistance = xDistance;
        snappedX = candidateCenterX - draggedWidth / 2;
      }
      if (yDistance <= ORGANIGRAM_SNAP_DISTANCE && yDistance < closestYDistance) {
        closestYDistance = yDistance;
        snappedY = candidateCenterY - draggedHeight / 2;
      }
    });

    return { x: snappedX, y: snappedY };
  }, [nodes, orthogonalNodeIds]);

  const handleNodeDrag = useCallback((_: unknown, node: RelationshipNode) => {
    if (!enableLayoutEditing || isSavingLayout) return;
    const snappedPosition = getSnappedNodePosition(node);
    if (arePositionsEqual(snappedPosition, node.position)) return;
    setNodes((currentNodes) => currentNodes.map((currentNode) => (
      currentNode.id === node.id ? { ...currentNode, position: snappedPosition } : currentNode
    )));
  }, [enableLayoutEditing, getSnappedNodePosition, isSavingLayout, setNodes]);

  const handleNodeDragStop = useCallback(
    (_: unknown, node: RelationshipNode) => {
      if (!enableLayoutEditing) return;
      if (isSavingLayout) return;
      const snappedNode = { ...node, position: getSnappedNodePosition(node) };
      setNodes((currentNodes) => currentNodes.map((currentNode) => (
        currentNode.id === node.id ? { ...currentNode, position: snappedNode.position } : currentNode
      )));
      localDirtyNodeIdsRef.current.add(node.id);
      setLayoutDirty(true);
      onLayoutPositionsChange?.(getPositionRecord(nodes.map((currentNode) => (
        currentNode.id === node.id ? snappedNode : currentNode
      ))));
    },
    [enableLayoutEditing, getSnappedNodePosition, isSavingLayout, nodes, onLayoutPositionsChange, setNodes],
  );

  useEffect(() => {
    let cancelled = false;

    if (isSavingLayout) {
      return () => {
        cancelled = true;
      };
    }

    setEdges(graphEdges);

    const focusGraph = () => {
      if (cancelled || !hasVisibleEntities) return;
      setTimeout(() => {
        if (!cancelled) requestAnimationFrame(() => fitView({ padding: 0.2, duration: 350 }));
      }, 300);
    };

    if (!graphInitializedRef.current) {
      graphInitializedRef.current = true;

      setNodes(graphNodes);

      if (!hasSavedPositions) {
        layoutGraph(graphNodes, layoutEdges, shapeTheme)
          .then((layoutedNodes) => {
            if (cancelled) return;
            setNodes(layoutedNodes);
            committedPositionsRef.current = getPositionMap(layoutedNodes);
            localDirtyNodeIdsRef.current.clear();
            setLayoutDirty(false);
            focusGraph();
          })
          .catch(() => {
            committedPositionsRef.current = getPositionMap(graphNodes);
            localDirtyNodeIdsRef.current.clear();
            setLayoutDirty(false);
            focusGraph();
          });
      } else {
        committedPositionsRef.current = getPositionMap(graphNodes);
        localDirtyNodeIdsRef.current.clear();
        setLayoutDirty(false);
        focusGraph();
      }

      return () => {
        cancelled = true;
      };
    }

    if (!hasSavedPositions && committedPositionsRef.current.size === 0) {
      layoutGraph(graphNodes, layoutEdges, shapeTheme)
        .then((layoutedNodes) => {
          if (cancelled) return;
          setNodes(layoutedNodes);
          committedPositionsRef.current = getPositionMap(layoutedNodes);
          localDirtyNodeIdsRef.current.clear();
          setLayoutDirty(false);
          focusGraph();
        })
        .catch(() => {
          committedPositionsRef.current = getPositionMap(graphNodes);
          localDirtyNodeIdsRef.current.clear();
          setLayoutDirty(false);
          focusGraph();
        });
    } else {
      const graphNodeIds = new Set(graphNodes.map((node) => node.id));
      const nextDirtyNodeIds = new Set(localDirtyNodeIdsRef.current);

      nextDirtyNodeIds.forEach((nodeId) => {
        if (!graphNodeIds.has(nodeId)) {
          nextDirtyNodeIds.delete(nodeId);
        }
      });

      graphNodes.forEach((graphNode) => {
        const entity = entitiesByNodeId.get(graphNode.id);
        const updatedBy = entity?.graphPositionUpdatedBy;
        if (updatedBy && updatedBy !== currentUserId) {
          nextDirtyNodeIds.delete(graphNode.id);
        }
      });

      localDirtyNodeIdsRef.current = nextDirtyNodeIds;
      setLayoutDirty(nextDirtyNodeIds.size > 0);

      setNodes((currentNodes) => {
        const currentNodesById = new Map(currentNodes.map((node) => [node.id, node]));
        const nextCommittedPositions = new Map(committedPositionsRef.current);

        const nextNodes = graphNodes.map((graphNode) => {
          const currentNode = currentNodesById.get(graphNode.id);
          const entity = entitiesByNodeId.get(graphNode.id);
          const updatedBy = entity?.graphPositionUpdatedBy;
          const updatedAt = entity?.graphPositionUpdatedAt;
          const isLocallyDirty = nextDirtyNodeIds.has(graphNode.id);
          const isOtherUserUpdate = !!updatedBy && updatedBy !== currentUserId;
          const committedPosition = committedPositionsRef.current.get(graphNode.id);
          const isStaleOwnSaveEcho = !!currentUserId
            && updatedBy === currentUserId
            && !!lastLocalLayoutSavedAtRef.current
            && updatedAt !== lastLocalLayoutSavedAtRef.current
            && !!committedPosition
            && !arePositionsEqual(graphNode.position, committedPosition);
          const isPreAttributionSaveEcho = !updatedBy
            && !!lastLocalLayoutSavedAtRef.current
            && !!committedPosition
            && !arePositionsEqual(graphNode.position, committedPosition);

          if (
            currentNode
            && (
              !entity?.graphPosition
              || isStaleOwnSaveEcho
              || isPreAttributionSaveEcho
              || (isLocallyDirty && !isOtherUserUpdate)
            )
          ) {
            return {
              ...graphNode,
              position: currentNode.position,
            };
          }

          nextCommittedPositions.set(graphNode.id, graphNode.position);

          return graphNode;
        });

        committedPositionsRef.current = nextCommittedPositions;

        return nextNodes;
      });
    }

    return () => {
      cancelled = true;
    };
  }, [
    currentUserId,
    entitiesByNodeId,
    fitView,
    graphEdges,
    graphNodes,
    hasSavedPositions,
    hasVisibleEntities,
    isSavingLayout,
    layoutEdges,
    setEdges,
    setNodes,
    shapeTheme,
  ]);

  useEffect(() => {
    if (fitViewDependency !== undefined && hasVisibleEntities) {
      // Small timeout allows ReactFlow to fully apply node updates before we calculate the bounding box
      const timer = setTimeout(() => {
        requestAnimationFrame(() => fitView({ padding: 0.2, duration: 350 }));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [fitViewDependency, fitView, hasVisibleEntities]);

  return (
    <>
      <ReactFlow
        colorMode={theme.palette.mode}
        nodes={focusedNodes}
        edges={focusedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={(_, node) => onEntityClick?.(node.data.entityType, node.data.entityId)}
        onEdgeClick={(_, edge) => {
          if (!onRelationshipClick) return;
          const relationship = relationships.find((item) => item.id === edge.id);
          if (relationship) onRelationshipClick(relationship);
        }}
        onNodeMouseEnter={(_, node) => setHoveredNodeId(node.id)}
        onNodeMouseLeave={() => setHoveredNodeId(null)}
        onConnectStart={(_, params) => {
          connectionOriginNodeIdRef.current = params.nodeId;
        }}
        onConnect={handleConnect}
        onConnectEnd={() => {
          connectionOriginNodeIdRef.current = null;
        }}
        connectionMode={ConnectionMode.Loose}
        connectionRadius={24}
        isValidConnection={isValidGraphConnection}
        edgeTypes={relationshipEdgeTypes}
        nodeTypes={relationshipNodeTypes}
        nodesDraggable={enableLayoutEditing && !isSavingLayout && !isResettingLayout}
        nodesConnectable={enableRelationshipEditing && !isRelationshipSaving}
        edgesFocusable={!!onRelationshipClick}
        fitView={hasVisibleEntities}
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        proOptions={{ hideAttribution: true }}
        onlyRenderVisibleElements
        style={{ background: mapSurface.background }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1}
          color={mapSurface.gridColor}
        />
        {!hasVisibleEntities && (
          <Panel position="top-center" style={{ top: '40%', pointerEvents: 'none' }}>
            <Paper variant="outlined" sx={{ px: 3, py: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('relationships.no_entity_types_selected')}
              </Typography>
            </Paper>
          </Panel>
        )}
        {showMiniMap && (
          <MiniMap
            nodeColor={(node) => String(node.data.color || theme.palette.grey[400])}
            nodeBorderRadius={shapeTheme === 'classic' ? NODE_WIDTH / 2 : 8}
            maskColor={mapSurface.miniMapMaskColor}
          />
        )}
        <Panel position="top-right">
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 1,
              minWidth: showShapeThemeSelector ? 210 : undefined,
              bgcolor: mapSurface.panelBackground,
              borderColor: mapSurface.panelBorderColor,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {showShapeThemeSelector && (
                <TextField
                  select
                  size="small"
                  label={t('relationships.shape_theme')}
                  value={shapeTheme}
                  onChange={(event) => handleShapeThemeChange(event.target.value as RelationshipMapShapeTheme)}
                  slotProps={{ select: { sx: { color: shapeTheme === 'classic' ? theme.palette.text.primary : theme.palette.common.white } } }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: shapeTheme === 'classic' ? undefined : alpha(theme.palette.common.white, 0.7),
                    },
                    '& .MuiOutlinedInput-root': {
                      color: shapeTheme === 'classic' ? undefined : theme.palette.common.white,
                      WebkitTextFillColor: shapeTheme === 'classic' ? undefined : theme.palette.common.white,
                      '& .MuiSelect-select, & .MuiInputBase-input': {
                        color: shapeTheme === 'classic' ? undefined : theme.palette.common.white,
                        WebkitTextFillColor: shapeTheme === 'classic' ? undefined : theme.palette.common.white,
                      },
                      '& fieldset': {
                        borderColor: mapSurface.panelBorderColor,
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      color: shapeTheme === 'classic' ? undefined : alpha(theme.palette.common.white, 0.78),
                    },
                  }}
                >
                  {RELATIONSHIP_MAP_SHAPE_THEMES.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`relationships.shape_themes.${option}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <FormControlLabel
                control={(
                  <Switch
                    checked={showMiniMap}
                    onChange={(event) => void handleShowMiniMapChange(event.target.checked)}
                    disabled={isUserProfileLoading}
                    size="small"
                  />
                )}
                label={t('relationships.show_minimap')}
                slotProps={{
                  typography: {
                    variant: 'caption',
                    color: shapeTheme === 'classic' ? 'text.secondary' : alpha(theme.palette.common.white, 0.72),
                  },
                }}
                sx={{ m: 0 }}
              />
            </Box>
          </Paper>
        </Panel>
        <Panel position="bottom-left">
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: mapSurface.panelBackground,
              borderColor: mapSurface.panelBorderColor,
              '& .MuiIconButton-root': {
                color: shapeTheme === 'classic' ? undefined : alpha(theme.palette.common.white, 0.82),
                '&.Mui-disabled': { color: shapeTheme === 'classic' ? theme.palette.action.disabled : alpha(theme.palette.common.white, 0.42) },
              },
            }}
          >
            <Tooltip title={t('relationships.zoom_in')} placement="right">
              <IconButton
                size="small"
                aria-label={t('relationships.zoom_in')}
                onClick={() => void zoomIn({ duration: 200 })}
                sx={{ borderRadius: 0 }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('relationships.zoom_out')} placement="right">
              <IconButton
                size="small"
                aria-label={t('relationships.zoom_out')}
                onClick={() => void zoomOut({ duration: 200 })}
                sx={{ borderRadius: 0, borderTop: 1, borderColor: 'divider' }}
              >
                <Remove fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('relationships.fit_view')} placement="right">
              <IconButton
                size="small"
                aria-label={t('relationships.fit_view')}
                onClick={() => void fitView({ padding: 0.2, duration: 250 })}
                disabled={!hasVisibleEntities}
                sx={{ borderRadius: 0, borderTop: 1, borderColor: 'divider' }}
              >
                <CenterFocusStrong fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </Panel>
        {enableLayoutEditing && (
          <Panel position="top-left">
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: mapSurface.panelBackground,
                borderColor: mapSurface.panelBorderColor,
                '& .MuiIconButton-root': {
                  color: shapeTheme === 'classic' ? undefined : alpha(theme.palette.common.white, 0.82),
                  '&.Mui-disabled': { color: shapeTheme === 'classic' ? theme.palette.action.disabled : alpha(theme.palette.common.white, 0.42) },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Tooltip title={t('relationships.reset_layout')} placement="right">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('relationships.reset_layout')}
                      onClick={() => void handleResetLayout()}
                      disabled={!hasSavedPositions || !hasVisibleEntities || isResettingLayout || isSavingLayout}
                      sx={{ alignSelf: 'flex-start', border: 1, borderColor: 'divider' }}
                    >
                      <RestartAlt fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('relationships.save_layout')} placement="right">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('relationships.save_layout')}
                      onClick={() => void persistLayout()}
                      disabled={!layoutDirty || isSavingLayout || isResettingLayout}
                      sx={{ alignSelf: 'flex-start', border: 1, borderColor: 'divider' }}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('relationships.discard_layout')} placement="right">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('relationships.discard_layout')}
                      onClick={() => discardLayoutChanges()}
                      disabled={!layoutDirty || isSavingLayout || isResettingLayout}
                      sx={{ alignSelf: 'flex-start', border: 1, borderColor: 'divider' }}
                    >
                      <Undo fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          </Panel>
        )}
      </ReactFlow>
      <Dialog
        open={status === 'blocked'}
        onClose={reset}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('relationships.unsaved_layout_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('relationships.unsaved_layout_description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2, flexWrap: 'nowrap' }}>
          <Button size="small" onClick={reset} disabled={isSavingLayout}>
            {t('relationships.unsaved_layout_stay')}
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<Undo fontSize="small" />}
            onClick={handleDiscardAndLeave}
            disabled={isSavingLayout}
          >
            {t('relationships.unsaved_layout_discard_and_leave')}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Save fontSize="small" />}
            onClick={() => void handleSaveAndLeave()}
            disabled={isSavingLayout}
          >
            {t('relationships.unsaved_layout_save_and_leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function RelationshipGraph(props: RelationshipGraphProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (props.entities.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ minHeight: 420, display: 'grid', placeItems: 'center', textAlign: 'center', p: 4 }}
      >
        <Typography color="text.secondary">{t('relationships.graph_no_entities')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        height: props.height,
        flexGrow: 1,
        minHeight: props.minHeight ?? 560,
        overflow: 'hidden',
        borderRadius: 2,
        '& .react-flow__handle': props.enableRelationshipEditing ? {
          width: 16,
          height: 16,
          border: `3px solid ${theme.palette.background.paper}`,
          bgcolor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.55)}, ${theme.shadows[3]}`,
          cursor: 'crosshair',
          opacity: 1,
          pointerEvents: 'all',
          zIndex: 50,
        } : {
          opacity: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <ReactFlowProvider>
        <RelationshipGraphCanvas {...props} />
      </ReactFlowProvider>
    </Paper>
  );
}
